import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Firestore, doc, getDoc, docData } from '@angular/fire/firestore';
import { Observable, from, map, switchMap, of, catchError } from 'rxjs';
import { UserRole, UserPermissions, UserProfile } from '../models/user-role.model';

/**
 * Service to manage and check user permissions
 * Combines Firebase custom claims (for roles) with Firestore data (for billing admin status)
 */
@Injectable({
    providedIn: 'root'
})
export class PermissionsService {
    private auth = inject(Auth);
    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);

    /**
     * Get user's role from custom claims in ID token
     */
    async getUserRole(): Promise<UserRole> {
        const user = this.auth.currentUser;
        if (!user) return 'viewer';

        try {
            const idTokenResult = await user.getIdTokenResult();
            return (idTokenResult.claims['role'] as UserRole) || 'viewer';
        } catch (error) {
            console.error('Error getting user role:', error);
            return 'viewer';
        }
    }

    /**
     * Get user's organization ID from custom claims in ID token
     * This provides fast access to the org ID without querying Firestore
     */
    async getUserOrgId(): Promise<string | null> {
        const user = this.auth.currentUser;
        if (!user) return null;

        try {
            const idTokenResult = await user.getIdTokenResult();
            return (idTokenResult.claims['orgId'] as string) || null;
        } catch (error) {
            console.error('Error getting user org ID:', error);
            return null;
        }
    }

    /**
     * Get user's role from Firestore (for checking if token needs refresh)
     */
    async getUserRoleFromFirestore(userId: string): Promise<UserRole> {
        try {
            const userDoc = await runInInjectionContext(this.injector, () => getDoc(doc(this.firestore, `users/${userId}`)));
            if (!userDoc.exists()) return 'viewer';

            const userData = userDoc.data() as UserProfile;
            return userData.role || 'viewer';
        } catch (error) {
            console.error('Error getting user role from Firestore:', error);
            return 'viewer';
        }
    }

    /**
     * Get user's billing admin status from Firestore
     */
    async isBillingAdministrator(userId: string): Promise<boolean> {
        try {
            const userDoc = await runInInjectionContext(this.injector, () => getDoc(doc(this.firestore, `users/${userId}`)));
            if (!userDoc.exists()) return false;

            const userData = userDoc.data() as UserProfile;
            return userData.isBillingAdministrator || false;
        } catch (error) {
            console.error('Error checking billing administrator status:', error);
            return false;
        }
    }

    /**
     * Get complete user permissions
     * Combines role from custom claims with billing admin status from Firestore
     */
    async getUserPermissions(userId: string): Promise<UserPermissions> {
        const role = await this.getUserRole();
        const isBillingAdmin = await this.isBillingAdministrator(userId);

        return this.calculatePermissions(role, isBillingAdmin);
    }

    /**
     * Calculate permissions based on role and billing admin status
     */
    private calculatePermissions(role: UserRole, isBillingAdmin: boolean): UserPermissions {
        // Support Staff (the role, not a Solenex support session): the
        // Conversations page and nothing else. Spelled out in full rather than
        // left to the role lists below, so a permission added later starts off
        // denied for them instead of quietly granted. Billing admin status is
        // ignored — billing pages are outside the Conversations page.
        if (role === 'support') {
            return {
                role,
                isBillingAdministrator: false,
                canViewInvoices: false,
                canManageSubscriptions: false,
                canSendMessages: true,
                canScheduleMessages: false,
                canManageTemplates: false,
                canManageAutomations: false,
                canManageUsers: false,
                canLinkWhatsApp: false,
                canLinkWebhooks: false,
                canManageApiApps: false,
                canManageWidgets: false,
                canManageSms: false,
                canViewConversations: true,
                canManageConversations: true,
                canCreateWorkflowCards: true,
                isConversationsOnly: true,
                canViewStats: false,
                canViewSettings: false
            };
        }

        const permissions: UserPermissions = {
            role,
            isBillingAdministrator: isBillingAdmin,

            // Billing permissions - billing admins or owner
            canViewInvoices: isBillingAdmin || role === 'owner',
            canManageSubscriptions: isBillingAdmin || role === 'owner',

            // Messaging permissions - sender and above
            canSendMessages: ['sender', 'editor', 'administrator', 'owner'].includes(role),

            // Scheduling and automation permissions - editor and above
            canScheduleMessages: ['editor', 'administrator', 'owner'].includes(role),
            canManageTemplates: ['editor', 'administrator', 'owner'].includes(role),
            canManageAutomations: ['editor', 'administrator', 'owner'].includes(role),

            // User management - administrator and owner only
            canManageUsers: ['administrator', 'owner'].includes(role),

            // WhatsApp/Webhook linking - owner only
            canLinkWhatsApp: role === 'owner',
            canLinkWebhooks: role === 'owner',

            // API credentials - owner only. A key can send messages on the
            // organisation's behalf and spends its Meta allowance.
            canManageApiApps: role === 'owner',

            // Website chat widgets - administrator and above. A widget key is a
            // public site key that only receives; it cannot send on the
            // organisation's behalf, so it need not be owner-gated.
            canManageWidgets: ['administrator', 'owner'].includes(role),

            // SMS provider link - owner only. The credential spends the
            // organisation's prepaid SMS credits on every send.
            canManageSms: role === 'owner',

            // Viewing permissions - all roles
            canViewConversations: true,

            // Conversation management - see all, assign to anyone, deal with,
            // recall. Administrators and owner (and Support Staff, above).
            canManageConversations: ['administrator', 'owner'].includes(role),

            // Planning Center workflow cards from a conversation - editor and
            // above (and Support Staff, above).
            canCreateWorkflowCards: ['editor', 'administrator', 'owner'].includes(role),

            isConversationsOnly: false,
            canViewStats: true,
            canViewSettings: ['administrator', 'owner'].includes(role)
        };

        return permissions;
    }

    /**
     * Check if user has a specific permission
     */
    async hasPermission(userId: string, permission: keyof UserPermissions): Promise<boolean> {
        const permissions = await this.getUserPermissions(userId);
        return permissions[permission] as boolean;
    }

    /**
     * Check if user can perform an action (alias for hasPermission)
     */
    async canPerform(userId: string, action: keyof UserPermissions): Promise<boolean> {
        return this.hasPermission(userId, action);
    }

    /**
     * Force refresh of user's ID token to get latest custom claims
     * Call this after role changes
     */
    async refreshUserToken(): Promise<void> {
        const user = this.auth.currentUser;
        if (user) {
            await user.getIdToken(true);
        }
    }
}
